import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Cookie, CheckCircle, Trash, UploadSimple, Question } from "@phosphor-icons/react";

const PLATFORM_META = {
  youtube: {
    label: "YouTube",
    hint: "Necesario para Shorts / vídeos que YouTube bloquea con 'Sign in to confirm you're not a bot'.",
    helpUrl: "https://chrome.google.com/webstore/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc",
  },
  instagram: {
    label: "Instagram",
    hint: "Permite descargar Reels privados o detrás de muro de login.",
    helpUrl: "https://chrome.google.com/webstore/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc",
  },
};

export default function CookiesManager() {
  const [status, setStatus] = useState({});
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/admin/cookies");
      setStatus(data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const upload = async (platform, file) => {
    if (!file) return;
    setBusy(platform);
    setMsg((m) => ({ ...m, [platform]: "" }));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.put(`/admin/cookies/${platform}`, form);
      setStatus((s) => ({ ...s, [platform]: data }));
      setMsg((m) => ({ ...m, [platform]: { ok: true, text: "Cookies guardadas" } }));
    } catch (e) {
      setMsg((m) => ({ ...m, [platform]: { ok: false, text: e.response?.data?.detail || "Error al subir" } }));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (platform) => {
    if (!confirm(`¿Eliminar cookies de ${PLATFORM_META[platform].label}?`)) return;
    setBusy(platform);
    try {
      await api.delete(`/admin/cookies/${platform}`);
      setStatus((s) => ({ ...s, [platform]: { present: false } }));
      setMsg((m) => ({ ...m, [platform]: { ok: true, text: "Eliminadas" } }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="rounded-3xl bg-white border border-[hsl(var(--border))] p-6"
      data-testid="cookies-manager"
    >
      <div className="flex items-center justify-between mb-5">
        <p className="eyebrow flex items-center gap-2">
          <Cookie size={16} className="text-[hsl(var(--muted-foreground))]" />
          cookies yt-dlp
        </p>
        <a
          href="https://chrome.google.com/webstore/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cookies-export-help-link"
          className="text-xs text-[hsl(var(--primary))] inline-flex items-center gap-1 hover:underline"
        >
          <Question size={12} /> Cómo exportarlas
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(PLATFORM_META).map(([platform, meta]) => {
          const st = status[platform] || {};
          const m = msg[platform];
          return (
            <PlatformCard
              key={platform}
              platform={platform}
              meta={meta}
              status={st}
              busy={busy === platform}
              message={m}
              onUpload={(file) => upload(platform, file)}
              onRemove={() => remove(platform)}
            />
          );
        })}
      </div>
    </section>
  );
}

function PlatformCard({ platform, meta, status, busy, message, onUpload, onRemove }) {
  const inputRef = useRef(null);
  return (
    <div
      className="rounded-2xl border border-[hsl(var(--border))] p-4 flex flex-col gap-3"
      data-testid={`cookies-card-${platform}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium">{meta.label}</p>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-snug mt-0.5">
            {meta.hint}
          </p>
        </div>
        {status.present ? (
          <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--secondary))]" data-testid={`cookies-status-${platform}`}>
            <CheckCircle size={14} weight="fill" /> Activo
          </span>
        ) : (
          <span className="text-xs text-[hsl(var(--muted-foreground))]" data-testid={`cookies-status-${platform}`}>No subido</span>
        )}
      </div>

      {status.present && (
        <p className="text-[10px] text-[hsl(var(--muted-foreground))]" data-testid={`cookies-metadata-${platform}`}>
          {status.bytes} bytes · actualizado {new Date(status.updated_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={inputRef}
          type="file"
          accept=".txt,text/plain"
          className="hidden"
          data-testid={`cookies-file-${platform}`}
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`cookies-upload-${platform}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--foreground))] text-white text-xs font-semibold btn-press disabled:opacity-50"
        >
          <UploadSimple size={14} /> {status.present ? "Reemplazar" : "Subir .txt"}
        </button>
        {status.present && (
          <button
            onClick={onRemove}
            disabled={busy}
            data-testid={`cookies-delete-${platform}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] text-xs font-semibold btn-press disabled:opacity-50"
          >
            <Trash size={13} /> Eliminar
          </button>
        )}
      </div>

      {message && (
        <p className={`text-xs ${message.ok ? "text-[hsl(var(--secondary))]" : "text-[hsl(var(--destructive))]"}`} data-testid={`cookies-message-${platform}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}
